/**
 * Sitz-Glocke — nur echte Vorgänge dieses Sitzes (Akte, Termin, Dokument-Share, Element-Raum).
 * Kein Mandantentext über bestehende Titel hinaus. Keine Events anderer Sitze.
 * Actor: nur echter Name vom handelnden Sitz; ohne Feld keine Fantasienamen.
 */

export type SitzGlockeKind = 'added' | 'removed';
export type SitzGlockeSource = 'akte' | 'termin' | 'dokument' | 'element';

export type SitzGlockeActor = {
  name: string;
  role?: string;
};

export type SitzGlockeEvent = {
  id: string;
  seatId: string;
  kind: SitzGlockeKind;
  source: SitzGlockeSource;
  refId: string;
  title: string;
  at: string;
  actorName?: string;
  actorRole?: string;
};

export type SitzGlockeSelfAction = {
  source: SitzGlockeSource;
  refId: string;
  kind: SitzGlockeKind;
};

export type SitzGlockeMemberSnap = {
  recordId: string;
  title: string;
};

export type SitzGlockeTerminSnap = {
  title: string;
  /** true = dieser Sitz ist Teilnehmer, nicht Owner. */
  asAttendee: boolean;
  ownerId?: string;
};

export type SitzGlockeDocSnap = {
  shareId: string;
  title: string;
  ownerId?: string;
};

export type SitzGlockeElementSnap = {
  title: string;
};

export type SitzGlockeSnapshot = {
  members: Record<string, SitzGlockeMemberSnap>;
  termine: Record<string, SitzGlockeTerminSnap>;
  docs: Record<string, SitzGlockeDocSnap>;
  elements: Record<string, SitzGlockeElementSnap>;
};

export type SitzGlockeState = {
  seatId: string;
  baselineReady: boolean;
  snapshot: SitzGlockeSnapshot;
  events: SitzGlockeEvent[];
  seenIds: string[];
  /** Selbst ausgelöste Vorgänge — einmalig beim nächsten Diff verbraucht. */
  selfSuppress: string[];
  /** Bearbeiter-Hinweis vom Portal-Aktionsort, keyed wie selfSuppress. */
  actorTips: Record<string, SitzGlockeActor>;
};

export const PORTAL_SITZ_GLOCKE_STORAGE_PREFIX = 'arkorbit.portal.sitzGlocke.v1:';
export const SITZ_GLOCKE_CHANGED_EVENT = 'arkorbit:sitz-glocke-changed';
export const SITZ_GLOCKE_OPEN_EVENT = 'arkorbit:sitz-glocke-open';

export function requestSitzGlockeOpen(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SITZ_GLOCKE_OPEN_EVENT));
}

function notifySitzGlockeChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SITZ_GLOCKE_CHANGED_EVENT));
}

const SAFE_ID = /^[A-Za-z0-9_-]{1,80}$/;
const MAX_EVENTS = 40;
const MAX_TIPS = 80;

function safeSeatId(raw: string): string {
  const id = raw.trim();
  return SAFE_ID.test(id) ? id : '';
}

function safeRefId(raw: string): string {
  const id = raw.trim();
  return SAFE_ID.test(id) ? id : '';
}

function safeTitle(raw: string): string {
  return raw.trim().slice(0, 120);
}

function safeActorName(raw: string): string {
  return raw.trim().slice(0, 80);
}

function safeActorRole(raw?: string): string | undefined {
  const role = (raw || '').trim().slice(0, 40);
  return role || undefined;
}

export function sitzGlockeStorageKey(seatId: string): string {
  const id = safeSeatId(seatId);
  return id ? `${PORTAL_SITZ_GLOCKE_STORAGE_PREFIX}${id}` : '';
}

export function selfSuppressKey(action: SitzGlockeSelfAction): string {
  return `${action.source}:${safeRefId(action.refId)}:${action.kind}`;
}

/** Anzeige-Rolle nur wenn am Sitz bekannt — kein festes „IT-Admin“. */
export function sitzGlockeRoleLabel(portalRole?: string): string | undefined {
  const role = (portalRole || '').trim();
  if (role === 'it_admin') return 'IT-Admin';
  if (role === 'member') return 'Anwalt';
  if (role === 'praktikant') return 'Praktikant / Leih';
  return undefined;
}

/** Echter Anzeigename vom Sitz; fehlt der Name, null — nichts erfinden. */
export function sitzGlockeActorFromSeat(seat?: {
  name?: string;
  email?: string;
  portal_role?: string;
} | null): SitzGlockeActor | null {
  if (!seat) return null;
  const name = safeActorName(seat.name || '') || safeActorName(seat.email || '');
  if (!name) return null;
  const role = sitzGlockeRoleLabel(seat.portal_role);
  return role ? { name, role } : { name };
}

export function formatSitzGlockeActorPhrase(actorName?: string, actorRole?: string): string {
  const name = safeActorName(actorName || '');
  if (!name) return '';
  const role = safeActorRole(actorRole);
  return role ? `${name} (${role})` : name;
}

/** „… hat dich hinzugefügt/entfernt“ — leer ohne echten Bearbeiter. */
export function sitzGlockeActorSentence(kind: SitzGlockeKind, actorName?: string, actorRole?: string): string {
  const phrase = formatSitzGlockeActorPhrase(actorName, actorRole);
  if (!phrase) return '';
  return kind === 'removed' ? `${phrase} hat dich entfernt` : `${phrase} hat dich hinzugefügt`;
}

function emptySnapshot(): SitzGlockeSnapshot {
  return { members: {}, termine: {}, docs: {}, elements: {} };
}

function normalizeActorTips(raw: unknown): Record<string, SitzGlockeActor> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, SitzGlockeActor> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!key || !value || typeof value !== 'object') continue;
    const row = value as { name?: unknown; role?: unknown };
    const name = typeof row.name === 'string' ? safeActorName(row.name) : '';
    if (!name) continue;
    const role = typeof row.role === 'string' ? safeActorRole(row.role) : undefined;
    out[key] = role ? { name, role } : { name };
    if (Object.keys(out).length >= MAX_TIPS) break;
  }
  return out;
}

function normalizeSnapshot(raw: unknown): SitzGlockeSnapshot {
  if (!raw || typeof raw !== 'object') return emptySnapshot();
  const snap = raw as Partial<SitzGlockeSnapshot>;
  return {
    members:
      snap.members && typeof snap.members === 'object'
        ? (snap.members as Record<string, SitzGlockeMemberSnap>)
        : {},
    termine:
      snap.termine && typeof snap.termine === 'object'
        ? (snap.termine as Record<string, SitzGlockeTerminSnap>)
        : {},
    docs: snap.docs && typeof snap.docs === 'object' ? (snap.docs as Record<string, SitzGlockeDocSnap>) : {},
    elements:
      snap.elements && typeof snap.elements === 'object'
        ? (snap.elements as Record<string, SitzGlockeElementSnap>)
        : {},
  };
}

export function emptySitzGlockeState(seatId: string): SitzGlockeState {
  return {
    seatId: safeSeatId(seatId),
    baselineReady: false,
    snapshot: emptySnapshot(),
    events: [],
    seenIds: [],
    selfSuppress: [],
    actorTips: {},
  };
}

export function loadSitzGlockeState(seatId: string): SitzGlockeState {
  const id = safeSeatId(seatId);
  const empty = emptySitzGlockeState(id);
  if (!id || typeof localStorage === 'undefined') return empty;
  try {
    const raw = localStorage.getItem(sitzGlockeStorageKey(id));
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<SitzGlockeState>;
    if (!parsed || typeof parsed !== 'object') return empty;
    if (parsed.seatId && parsed.seatId !== id) return empty;
    const events = Array.isArray(parsed.events)
      ? parsed.events
          .filter((row): row is SitzGlockeEvent => Boolean(row && typeof row === 'object'))
          .filter((row) => row.seatId === id)
          .map((row) => ({
            ...row,
            actorName: typeof row.actorName === 'string' ? safeActorName(row.actorName) || undefined : undefined,
            actorRole: typeof row.actorRole === 'string' ? safeActorRole(row.actorRole) : undefined,
          }))
          .slice(0, MAX_EVENTS)
      : [];
    const seenIds = Array.isArray(parsed.seenIds)
      ? parsed.seenIds.filter((x): x is string => typeof x === 'string' && x.trim().length > 0).slice(0, 200)
      : [];
    const selfSuppress = Array.isArray(parsed.selfSuppress)
      ? parsed.selfSuppress.filter((x): x is string => typeof x === 'string').slice(0, 40)
      : [];
    return {
      seatId: id,
      baselineReady: parsed.baselineReady === true,
      snapshot: normalizeSnapshot(parsed.snapshot),
      events,
      seenIds,
      selfSuppress,
      actorTips: normalizeActorTips(parsed.actorTips),
    };
  } catch {
    return empty;
  }
}

export function saveSitzGlockeState(state: SitzGlockeState): void {
  const id = safeSeatId(state.seatId);
  if (!id || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      sitzGlockeStorageKey(id),
      JSON.stringify({
        seatId: id,
        baselineReady: state.baselineReady === true,
        snapshot: state.snapshot,
        events: state.events.filter((e) => e.seatId === id).slice(0, MAX_EVENTS),
        seenIds: state.seenIds.slice(0, 200),
        selfSuppress: state.selfSuppress.slice(0, 40),
        actorTips: Object.fromEntries(Object.entries(state.actorTips || {}).slice(0, MAX_TIPS)),
      }),
    );
    notifySitzGlockeChanged();
  } catch {
    /* Speicher voll — Glocke bleibt ohne Persistenz nutzbar. */
  }
}

/** Eigene Aktion am eigenen Sitz: kein Überraschungs-Eintrag (bestehende Regel). */
export function noteSitzGlockeSelfAction(seatId: string, action: SitzGlockeSelfAction): void {
  const id = safeSeatId(seatId);
  if (!id || !safeRefId(action.refId)) return;
  const state = loadSitzGlockeState(id);
  const key = selfSuppressKey(action);
  if (!state.selfSuppress.includes(key)) state.selfSuppress = [...state.selfSuppress, key];
  saveSitzGlockeState(state);
}

/**
 * Portal hat die Aktion ausgeführt: Bearbeiter am Ziel-Sitz merken.
 * Ziel === Handelnder → nur Suppress (keine Überraschung).
 * Ohne echten Namen: kein Tip, kein Fantasiename.
 */
export function noteSitzGlockePortalAction(args: {
  targetSeatId: string;
  actorSeatId: string;
  actorName?: string;
  actorRole?: string;
  source: SitzGlockeSource;
  refId: string;
  kind: SitzGlockeKind;
}): void {
  const targetId = safeSeatId(args.targetSeatId);
  const actorId = safeSeatId(args.actorSeatId);
  const refId = safeRefId(args.refId);
  if (!targetId || !refId) return;
  const action: SitzGlockeSelfAction = { source: args.source, refId, kind: args.kind };
  if (targetId === actorId) {
    noteSitzGlockeSelfAction(targetId, action);
    return;
  }
  const name = safeActorName(args.actorName || '');
  if (!name) return;
  const state = loadSitzGlockeState(targetId);
  const key = selfSuppressKey(action);
  const role = safeActorRole(args.actorRole);
  state.actorTips = {
    ...state.actorTips,
    [key]: role ? { name, role } : { name },
  };
  saveSitzGlockeState(state);
}

export function unseenSitzGlockeCount(state: SitzGlockeState): number {
  const seen = new Set(state.seenIds);
  return state.events.filter((e) => e.seatId === state.seatId && !seen.has(e.id)).length;
}

export function markSitzGlockeSeen(state: SitzGlockeState): SitzGlockeState {
  const ids = state.events.filter((e) => e.seatId === state.seatId).map((e) => e.id);
  const merged = Array.from(new Set([...state.seenIds, ...ids]));
  return { ...state, seenIds: merged };
}

export function sitzGlockeKindLabel(kind: SitzGlockeKind): 'Hinzugefügt' | 'Entfernt' {
  return kind === 'removed' ? 'Entfernt' : 'Hinzugefügt';
}

export function sitzGlockeSourceLabel(source: SitzGlockeSource): string {
  if (source === 'termin') return 'Termin';
  if (source === 'dokument') return 'Dokument';
  if (source === 'element') return 'Element';
  return 'Akte';
}

/** Nur Ereignisse dieses Sitzes. Fremde seatId fallen weg. */
export function filterSitzGlockeEventsForSeat(
  events: readonly SitzGlockeEvent[],
  seatId: string,
): SitzGlockeEvent[] {
  const id = safeSeatId(seatId);
  if (!id) return [];
  return events.filter((e) => e.seatId === id);
}

export type SitzGlockeLiveMember = {
  recordId: string;
  akteId: string;
  title: string;
  seatId: string;
};

export type SitzGlockeLiveTermin = {
  id: string;
  title: string;
  ownerId: string;
  attendeeIds: readonly string[];
  actorName?: string;
  actorRole?: string;
};

export type SitzGlockeLiveDoc = {
  documentId: string;
  shareId: string;
  title: string;
  seatId: string;
  ownerId?: string;
  actorName?: string;
  actorRole?: string;
};

export type SitzGlockeLiveElement = {
  roomId: string;
  title: string;
  seatId: string;
  actorName?: string;
  actorRole?: string;
};

function takeActor(
  tips: Record<string, SitzGlockeActor>,
  key: string,
  liveName?: string,
  liveRole?: string,
): SitzGlockeActor | undefined {
  const tip = tips[key];
  if (tip?.name) {
    delete tips[key];
    return tip;
  }
  const name = safeActorName(liveName || '');
  if (!name) return undefined;
  const role = safeActorRole(liveRole);
  return role ? { name, role } : { name };
}

function pushEvent(
  fresh: SitzGlockeEvent[],
  tips: Record<string, SitzGlockeActor>,
  suppress: Set<string>,
  args: {
    id: string;
    seatId: string;
    kind: SitzGlockeKind;
    source: SitzGlockeSource;
    refId: string;
    title: string;
    at: string;
    tipKey: string;
    liveName?: string;
    liveRole?: string;
  },
): void {
  if (suppress.has(args.tipKey)) {
    suppress.delete(args.tipKey);
    delete tips[args.tipKey];
    return;
  }
  const actor = takeActor(tips, args.tipKey, args.liveName, args.liveRole);
  fresh.push({
    id: args.id,
    seatId: args.seatId,
    kind: args.kind,
    source: args.source,
    refId: args.refId,
    title: args.title,
    at: args.at,
    ...(actor?.name ? { actorName: actor.name } : {}),
    ...(actor?.role ? { actorRole: actor.role } : {}),
  });
}

/**
 * Diff gegen Snapshot. Erster Lauf = Baseline ohne Ereignisse (leere Liste bleibt ruhig).
 * selfSuppress verhindert eigene Aktionen, wenn der Auslöser erkennbar ist.
 */
export function mergeSitzGlockeFromLive(args: {
  seatId: string;
  prev: SitzGlockeState;
  members: readonly SitzGlockeLiveMember[];
  termine: readonly SitzGlockeLiveTermin[];
  docs?: readonly SitzGlockeLiveDoc[];
  elements?: readonly SitzGlockeLiveElement[];
  nowIso?: string;
}): SitzGlockeState {
  const seatId = safeSeatId(args.seatId);
  if (!seatId) return emptySitzGlockeState('');
  const now = args.nowIso || new Date().toISOString();
  const nextMembers: Record<string, SitzGlockeMemberSnap> = {};
  for (const row of args.members) {
    if (row.seatId !== seatId) continue;
    const akteId = safeRefId(row.akteId);
    const recordId = safeRefId(row.recordId);
    if (!akteId || !recordId) continue;
    nextMembers[akteId] = { recordId, title: safeTitle(row.title) || 'Akte' };
  }
  const nextTermine: Record<string, SitzGlockeTerminSnap> = {};
  const terminActors: Record<string, SitzGlockeActor> = {};
  for (const row of args.termine) {
    const eventId = safeRefId(row.id);
    if (!eventId) continue;
    const attendees = (row.attendeeIds || []).map((x) => safeSeatId(x)).filter(Boolean);
    const owner = safeSeatId(row.ownerId);
    const asAttendee = attendees.includes(seatId) && owner !== seatId;
    if (!asAttendee) continue;
    nextTermine[eventId] = {
      title: safeTitle(row.title) || 'Termin',
      asAttendee: true,
      ownerId: owner || undefined,
    };
    const actorName = safeActorName(row.actorName || '');
    if (actorName) {
      const role = safeActorRole(row.actorRole);
      terminActors[eventId] = role ? { name: actorName, role } : { name: actorName };
    }
  }

  const nextDocs: Record<string, SitzGlockeDocSnap> = {};
  const docActors: Record<string, SitzGlockeActor> = {};
  for (const row of args.docs || []) {
    if (row.seatId !== seatId) continue;
    const documentId = safeRefId(row.documentId);
    const shareId = safeRefId(row.shareId);
    if (!documentId || !shareId) continue;
    nextDocs[documentId] = {
      shareId,
      title: safeTitle(row.title) || 'Dokument',
      ownerId: safeSeatId(row.ownerId || '') || undefined,
    };
    const actorName = safeActorName(row.actorName || '');
    if (actorName) {
      const role = safeActorRole(row.actorRole);
      docActors[documentId] = role ? { name: actorName, role } : { name: actorName };
    }
  }

  const nextElements: Record<string, SitzGlockeElementSnap> = {};
  const elementActors: Record<string, SitzGlockeActor> = {};
  for (const row of args.elements || []) {
    if (row.seatId !== seatId) continue;
    const roomId = safeRefId(row.roomId);
    if (!roomId) continue;
    nextElements[roomId] = { title: safeTitle(row.title) || 'Element' };
    const actorName = safeActorName(row.actorName || '');
    if (actorName) {
      const role = safeActorRole(row.actorRole);
      elementActors[roomId] = role ? { name: actorName, role } : { name: actorName };
    }
  }

  const nextSnap: SitzGlockeSnapshot = {
    members: nextMembers,
    termine: nextTermine,
    docs: nextDocs,
    elements: nextElements,
  };

  if (!args.prev.baselineReady) {
    return {
      seatId,
      baselineReady: true,
      snapshot: nextSnap,
      events: [],
      seenIds: args.prev.seenIds,
      selfSuppress: [],
      actorTips: args.prev.actorTips || {},
    };
  }

  const suppress = new Set(args.prev.selfSuppress);
  const tips = { ...(args.prev.actorTips || {}) };
  const fresh: SitzGlockeEvent[] = [];

  for (const [akteId, snap] of Object.entries(nextMembers)) {
    if (args.prev.snapshot.members[akteId]) continue;
    pushEvent(fresh, tips, suppress, {
      id: `akte:${snap.recordId}:added`,
      seatId,
      kind: 'added',
      source: 'akte',
      refId: akteId,
      title: snap.title,
      at: now,
      tipKey: selfSuppressKey({ source: 'akte', refId: akteId, kind: 'added' }),
    });
  }
  for (const [akteId, snap] of Object.entries(args.prev.snapshot.members || {})) {
    if (nextMembers[akteId]) continue;
    pushEvent(fresh, tips, suppress, {
      id: `akte:${snap.recordId}:removed`,
      seatId,
      kind: 'removed',
      source: 'akte',
      refId: akteId,
      title: snap.title,
      at: now,
      tipKey: selfSuppressKey({ source: 'akte', refId: akteId, kind: 'removed' }),
    });
  }

  for (const [eventId, snap] of Object.entries(nextTermine)) {
    if (args.prev.snapshot.termine?.[eventId]) continue;
    const live = terminActors[eventId];
    pushEvent(fresh, tips, suppress, {
      id: `termin:${eventId}:added`,
      seatId,
      kind: 'added',
      source: 'termin',
      refId: eventId,
      title: snap.title,
      at: now,
      tipKey: selfSuppressKey({ source: 'termin', refId: eventId, kind: 'added' }),
      liveName: live?.name,
      liveRole: live?.role,
    });
  }
  for (const [eventId, snap] of Object.entries(args.prev.snapshot.termine || {})) {
    if (nextTermine[eventId]) continue;
    pushEvent(fresh, tips, suppress, {
      id: `termin:${eventId}:removed`,
      seatId,
      kind: 'removed',
      source: 'termin',
      refId: eventId,
      title: snap.title,
      at: now,
      tipKey: selfSuppressKey({ source: 'termin', refId: eventId, kind: 'removed' }),
    });
  }

  for (const [documentId, snap] of Object.entries(nextDocs)) {
    if (args.prev.snapshot.docs?.[documentId]) continue;
    const live = docActors[documentId];
    pushEvent(fresh, tips, suppress, {
      id: `dokument:${snap.shareId}:added`,
      seatId,
      kind: 'added',
      source: 'dokument',
      refId: documentId,
      title: snap.title,
      at: now,
      tipKey: selfSuppressKey({ source: 'dokument', refId: documentId, kind: 'added' }),
      liveName: live?.name,
      liveRole: live?.role,
    });
  }
  for (const [documentId, snap] of Object.entries(args.prev.snapshot.docs || {})) {
    if (nextDocs[documentId]) continue;
    pushEvent(fresh, tips, suppress, {
      id: `dokument:${snap.shareId}:removed`,
      seatId,
      kind: 'removed',
      source: 'dokument',
      refId: documentId,
      title: snap.title,
      at: now,
      tipKey: selfSuppressKey({ source: 'dokument', refId: documentId, kind: 'removed' }),
    });
  }

  for (const [roomId, snap] of Object.entries(nextElements)) {
    if (args.prev.snapshot.elements?.[roomId]) continue;
    const live = elementActors[roomId];
    pushEvent(fresh, tips, suppress, {
      id: `element:${roomId}:added`,
      seatId,
      kind: 'added',
      source: 'element',
      refId: roomId,
      title: snap.title,
      at: now,
      tipKey: selfSuppressKey({ source: 'element', refId: roomId, kind: 'added' }),
      liveName: live?.name,
      liveRole: live?.role,
    });
  }
  for (const [roomId, snap] of Object.entries(args.prev.snapshot.elements || {})) {
    if (nextElements[roomId]) continue;
    pushEvent(fresh, tips, suppress, {
      id: `element:${roomId}:removed`,
      seatId,
      kind: 'removed',
      source: 'element',
      refId: roomId,
      title: snap.title,
      at: now,
      tipKey: selfSuppressKey({ source: 'element', refId: roomId, kind: 'removed' }),
    });
  }

  const byId = new Map<string, SitzGlockeEvent>();
  for (const row of args.prev.events) {
    if (row.seatId === seatId) byId.set(row.id, row);
  }
  for (const row of fresh) byId.set(row.id, row);

  return {
    seatId,
    baselineReady: true,
    snapshot: nextSnap,
    events: Array.from(byId.values())
      .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
      .slice(0, MAX_EVENTS),
    seenIds: args.prev.seenIds,
    selfSuppress: Array.from(suppress),
    actorTips: tips,
  };
}
